// Misión → Mis pacientes (/mision/mis-pacientes).
//
// Lo que ve el presidente de una misión: los pacientes de SUS misiones, con su
// ficha, su historia clínica, sus recetas y sus órdenes de examen. Sólo
// consulta — esta pantalla no tiene una sola acción que escriba.
//
// El alcance NO lo pone esta pantalla: lo pone la base (script 853). Aunque
// alguien llamara las RPC a mano, sólo devuelven lo que cuelga de las misiones
// asignadas a ese usuario, y abrir una ficha deja constancia en la bitácora.
//
// Recetas, órdenes de examen, «Atenciones este mes» y «Misiones» son solo para
// nivel ≥ 5 (script 909): la base no se los entrega al resto, y aquí no se pintan.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ClipboardList, FileText, FlaskConical, Loader2, Search, Stethoscope, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { fieldCls, medRpc } from '@/features/medical/shared';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import { ExportarBotones } from '@/lib/utils/exportarTabla';
import type { Celda } from '@/lib/utils/exportarTabla';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { LOCALE } from '@/lib/utils/regional';

interface MisionRef { id: string; codigo: string | null; nombre: string }
interface Resumen { misiones: MisionRef[]; pacientes: number; atenciones_mes: number }
interface PacienteRow {
  id: string; codigo: string | null; nombre: string; identificacion: string | null;
  genero: string | null; edad: number | null; mision: string | null; mision_codigo: string | null;
  ultima_atencion: string | null; atenciones: number; recetas: number; ordenes: number;
}
interface Atencion {
  id: string; fecha: string; motivo: string | null;
  subjetivo: string | null; objetivo: string | null; analisis: string | null; plan: string | null;
  medico: string | null; especialidad: string | null;
  diagnosticos: { cie10: string | null; descripcion: string; tipo: string | null; principal: boolean }[];
  signos: { presion: string | null; fc: number | null; fr: number | null; temperatura: number | null;
            spo2: number | null; peso: number | null; talla: number | null } | null;
}
interface Ficha {
  paciente: Record<string, string | number | null>;
  atenciones: Atencion[];
  diagnosticos_activos: { cie10: string | null; descripcion: string }[];
}
interface RecetaRow {
  id: string; codigo: string; fecha: string; estado: string; diagnostico: string | null;
  indicaciones: string | null; medico: string | null; valido_hasta: string | null;
  items: { medicamento: string; comercial: string | null; concentracion: string | null;
           dosis: string | null; frecuencia: string | null; duracion_dias: number | null;
           cantidad: string | null; via: string | null; indicaciones: string | null }[];
}
interface OrdenRow {
  id: string; codigo: string; fecha: string; estado: string; prioridad: string | null;
  diagnostico: string | null; laboratorio: string | null; medico: string | null;
  items: { nombre: string; categoria: string; estado: string;
           resultados: { parametro: string | null; valor: string | null; unidad: string | null;
                         flag: string | null; ref_min: number | null; ref_max: number | null }[] }[];
}

const fecha = (s: string | null) => (s ? new Date(s).toLocaleDateString(LOCALE, { dateStyle: 'medium' }) : '—');

export function MisPacientes() {
  const [busqueda, setBusqueda] = useState('');
  const [sel, setSel] = useState<PacienteRow | null>(null);
  const { isAdmin } = usePermissions();
  const admin = isAdmin();

  const { data: resumen } = useQuery<Resumen>({
    queryKey: ['mision-resumen'], queryFn: () => medRpc<Resumen>('med_mision_mi_resumen'),
  });
  const { data: pacientes = [], isFetching } = useQuery<PacienteRow[]>({
    queryKey: ['mision-pacientes', busqueda],
    queryFn: () => medRpc<PacienteRow[]>('med_mision_pacientes', { _busqueda: busqueda || null }),
    staleTime: 0, refetchOnMount: 'always',
  });

  const accessor = (p: PacienteRow, col: string) => {
    switch (col) {
      case 'nombre': return p.nombre;
      case 'identificacion': return p.identificacion;
      case 'edad': return p.edad;
      case 'mision': return p.mision;
      case 'ultima': return p.ultima_atencion;
      case 'atenciones': return Number(p.atenciones);
      case 'recetas': return Number(p.recetas);
      case 'ordenes': return Number(p.ordenes);
      default: return null;
    }
  };
  const sort = useSort(pacientes, accessor, 'nombre');

  if (sel) return <DetallePaciente p={sel} admin={admin} onVolver={() => setSel(null)} />;

  const filas: Celda[][] = sort.sorted.map((p) => [
    p.nombre, p.identificacion, p.edad, p.mision, fecha(p.ultima_atencion),
    p.atenciones, ...(admin ? [p.recetas, p.ordenes] : []),
  ]);

  const sinMisiones = resumen && resumen.misiones.length === 0;

  return (
    <div className="p-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Users className="h-5 w-5 text-primary" /> Mis pacientes
          </h1>
          <p className="text-sm text-muted-foreground">
            {resumen?.misiones.length
              ? <>Misión(es): {resumen.misiones.map((m) => m.nombre).join(' · ')}</>
              : 'Consulta de los pacientes de sus misiones'}
          </p>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input className={`${fieldCls} w-64 pl-8`} placeholder="Buscar por nombre o identificación"
                 value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
        </div>
      </div>

      {sinMisiones && (
        <Card className="mb-3 border-amber-300 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="py-3 text-sm text-amber-900 dark:text-amber-100">
            Su usuario no tiene misiones asignadas, por eso no aparece ningún paciente. Un
            administrador puede asignárselas desde Administración → Usuarios.
          </CardContent>
        </Card>
      )}

      {resumen && !sinMisiones && (
        <div className={`mb-3 grid gap-3 ${admin ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4'}`}>
          <Card><CardContent className="py-3">
            <div className="text-xs text-muted-foreground">Pacientes</div>
            <div className="text-2xl font-semibold">{resumen.pacientes}</div>
          </CardContent></Card>
          {admin && (
            <Card><CardContent className="py-3">
              <div className="text-xs text-muted-foreground">Atenciones este mes</div>
              <div className="text-2xl font-semibold">{resumen.atenciones_mes}</div>
            </CardContent></Card>
          )}
          {admin && (
            <Card><CardContent className="py-3">
              <div className="text-xs text-muted-foreground">Misiones</div>
              <div className="text-2xl font-semibold">{resumen.misiones.length}</div>
            </CardContent></Card>
          )}
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
          <CardTitle className="text-base">{sort.sorted.length} paciente(s)</CardTitle>
          <ExportarBotones
            nombre="mis-pacientes" titulo="Mis pacientes"
            headers={['Paciente', 'Identificación', 'Edad', 'Misión', 'Última atención', 'Atenciones', ...(admin ? ['Recetas', 'Órdenes'] : [])]}
            colsDerecha={admin ? [2, 5, 6, 7] : [2, 5]} filas={filas}
          />
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="w-10 py-2 pr-3 text-right">N°</th>
                  <SortTh col="nombre" sort={sort}>Paciente</SortTh>
                  <SortTh col="identificacion" sort={sort}>Identificación</SortTh>
                  <SortTh col="edad" sort={sort} align="right">Edad</SortTh>
                  <SortTh col="mision" sort={sort}>Misión</SortTh>
                  <SortTh col="ultima" sort={sort}>Última atención</SortTh>
                  <SortTh col="atenciones" sort={sort} align="right">Atenciones</SortTh>
                  {admin && <SortTh col="recetas" sort={sort} align="right">Recetas</SortTh>}
                  {admin && <SortTh col="ordenes" sort={sort} align="right">Órdenes</SortTh>}
                </tr>
              </thead>
              <tbody>
                {sort.sorted.map((p, i) => (
                  <tr key={p.id} className="cursor-pointer border-b last:border-0 hover:bg-muted/50"
                      onClick={() => setSel(p)}>
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-medium">{p.nombre}</td>
                    <td className="py-2 pr-3 font-mono">{p.identificacion ?? '—'}</td>
                    <td className="py-2 pr-3 text-right">{p.edad ?? '—'}</td>
                    <td className="py-2 pr-3">{p.mision ?? '—'}</td>
                    <td className="py-2 pr-3">{fecha(p.ultima_atencion)}</td>
                    <td className="py-2 pr-3 text-right">{p.atenciones}</td>
                    {admin && <td className="py-2 pr-3 text-right">{p.recetas}</td>}
                    {admin && <td className="py-2 pr-3 text-right">{p.ordenes}</td>}
                  </tr>
                ))}
                {sort.sorted.length > 0 && (
                  <tr className="border-t-2 bg-muted/30 font-semibold">
                    <td className="py-2 pr-3" colSpan={6}>Totales ({sort.sorted.length})</td>
                    <td className="py-2 pr-3 text-right">{sort.sorted.reduce((a, p) => a + Number(p.atenciones), 0)}</td>
                    {admin && <td className="py-2 pr-3 text-right">{sort.sorted.reduce((a, p) => a + Number(p.recetas), 0)}</td>}
                    {admin && <td className="py-2 pr-3 text-right">{sort.sorted.reduce((a, p) => a + Number(p.ordenes), 0)}</td>}
                  </tr>
                )}
                {sort.sorted.length === 0 && !isFetching && !sinMisiones && (
                  <tr><td colSpan={admin ? 9 : 7} className="py-6 text-center text-muted-foreground">
                    Ningún paciente coincide con la búsqueda.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const TABS = [
  { v: 'ficha', l: 'Ficha e historia clínica', icon: Stethoscope },
  { v: 'recetas', l: 'Recetas', icon: FileText },
  { v: 'ordenes', l: 'Órdenes de examen', icon: FlaskConical },
] as const;

function DetallePaciente({ p, admin, onVolver }: { p: PacienteRow; admin: boolean; onVolver: () => void }) {
  const [tab, setTab] = useState<(typeof TABS)[number]['v']>('ficha');
  // Recetas y órdenes: solo nivel ≥ 5 (909)
  const tabs = admin ? TABS : TABS.filter((t) => t.v === 'ficha');

  const { data: ficha, isFetching: cargandoFicha } = useQuery<Ficha>({
    queryKey: ['mision-ficha', p.id], queryFn: () => medRpc<Ficha>('med_mision_paciente', { _paciente: p.id }),
  });
  const { data: recetas = [] } = useQuery<RecetaRow[]>({
    queryKey: ['mision-recetas', p.id], queryFn: () => medRpc<RecetaRow[]>('med_mision_recetas', { _paciente: p.id }),
    enabled: admin && tab === 'recetas',
  });
  const { data: ordenes = [] } = useQuery<OrdenRow[]>({
    queryKey: ['mision-ordenes', p.id], queryFn: () => medRpc<OrdenRow[]>('med_mision_ordenes', { _paciente: p.id }),
    enabled: admin && tab === 'ordenes',
  });

  const d = ficha?.paciente ?? {};

  return (
    <div className="p-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onVolver}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Volver
          </Button>
          <div>
            <h1 className="text-xl font-semibold">{p.nombre}</h1>
            <p className="text-sm text-muted-foreground">
              {p.identificacion ?? 's/i'} · {p.edad ?? '—'} años · {p.mision ?? 'sin misión'}
            </p>
          </div>
        </div>
        <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">Sólo consulta</span>
      </div>

      <div className="mb-3 flex flex-wrap gap-1">
        {tabs.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)}
                  className={`flex items-center gap-1 rounded-full px-3 py-1 text-sm ${tab === t.v
                    ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/70'}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.l}
          </button>
        ))}
      </div>

      {cargandoFicha && !ficha && (
        <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>
      )}

      {tab === 'ficha' && ficha && (
        <div className="space-y-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Datos del paciente</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm md:grid-cols-4">
              <Dato l="Código" v={d.codigo} /><Dato l="Género" v={d.genero} />
              <Dato l="Tipo de sangre" v={d.tipo_sangre} /><Dato l="Aseguradora" v={d.aseguradora} />
              <Dato l="Teléfono" v={d.celular ?? d.telefono} /><Dato l="Email" v={d.email} />
              <Dato l="Ciudad" v={d.ciudad} /><Dato l="Contacto emergencia" v={d.contacto_emergencia} />
              <div className="col-span-2 md:col-span-4"><Dato l="Alergias" v={d.alergias} /></div>
              <div className="col-span-2 md:col-span-4"><Dato l="Antecedentes" v={d.antecedentes} /></div>
              <div className="col-span-2 md:col-span-4"><Dato l="Medicación actual" v={d.medicamentos_actuales} /></div>
            </CardContent>
          </Card>

          {ficha.diagnosticos_activos.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Diagnósticos registrados</CardTitle></CardHeader>
              <CardContent className="flex flex-wrap gap-1 text-sm">
                {ficha.diagnosticos_activos.map((dx, i) => (
                  <span key={i} className="rounded-full bg-muted px-2 py-0.5 text-xs">
                    {dx.cie10 ? `${dx.cie10} · ` : ''}{dx.descripcion}
                  </span>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Historia clínica ({ficha.atenciones.length} atención/es)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {ficha.atenciones.length === 0 && (
                <p className="py-4 text-center text-sm text-muted-foreground">Sin atenciones registradas.</p>
              )}
              {ficha.atenciones.map((a) => (
                <div key={a.id} className="rounded border p-3 text-sm">
                  <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium">{fecha(a.fecha)}{a.motivo ? ` · ${a.motivo}` : ''}</span>
                    <span className="text-xs text-muted-foreground">
                      {a.medico ?? '—'}{a.especialidad ? ` · ${a.especialidad}` : ''}
                    </span>
                  </div>
                  {a.diagnosticos.length > 0 && (
                    <p className="mb-1 text-xs">
                      <b>Dx:</b> {a.diagnosticos.map((x) => `${x.cie10 ?? ''} ${x.descripcion}`.trim()).join(' · ')}
                    </p>
                  )}
                  {a.signos && (a.signos.presion || a.signos.fc || a.signos.temperatura) && (
                    <p className="mb-1 text-xs text-muted-foreground">
                      {a.signos.presion && `PA ${a.signos.presion} · `}
                      {a.signos.fc && `FC ${a.signos.fc} · `}
                      {a.signos.temperatura && `T° ${a.signos.temperatura} · `}
                      {a.signos.spo2 && `SpO₂ ${a.signos.spo2}% · `}
                      {a.signos.peso && `${a.signos.peso} kg`}
                    </p>
                  )}
                  <Bloque l="Subjetivo" v={a.subjetivo} /><Bloque l="Objetivo" v={a.objetivo} />
                  <Bloque l="Análisis" v={a.analisis} /><Bloque l="Plan" v={a.plan} />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === 'recetas' && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">{recetas.length} receta(s)</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {recetas.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">Sin recetas.</p>}
            {recetas.map((r) => (
              <div key={r.id} className="rounded border p-3 text-sm">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">{r.codigo} · {fecha(r.fecha)}</span>
                  <span className="text-xs text-muted-foreground">{r.medico ?? '—'} · {r.estado}</span>
                </div>
                {r.diagnostico && <p className="mb-1 text-xs"><b>Dx:</b> {r.diagnostico}</p>}
                {r.items.map((it, i) => (
                  <p key={i} className="text-xs">
                    {i + 1}. {it.medicamento}{it.comercial ? ` (${it.comercial})` : ''}
                    {it.concentracion ? ` ${it.concentracion}` : ''}
                    {it.dosis || it.frecuencia ? ` — ${[it.dosis, it.frecuencia, it.duracion_dias ? `${it.duracion_dias} días` : null].filter(Boolean).join(' · ')}` : ''}
                  </p>
                ))}
                {r.indicaciones && <p className="mt-1 text-xs text-muted-foreground">{r.indicaciones}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {tab === 'ordenes' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardList className="h-4 w-4" /> {ordenes.length} orden(es) de examen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {ordenes.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">Sin órdenes.</p>}
            {ordenes.map((o) => (
              <div key={o.id} className="rounded border p-3 text-sm">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">{o.codigo} · {fecha(o.fecha)}</span>
                  <span className="text-xs text-muted-foreground">{o.medico ?? '—'} · {o.estado}</span>
                </div>
                {o.items.map((it, i) => (
                  <div key={i} className="mt-1">
                    <p className="text-xs"><b>{it.nombre}</b> <span className="text-muted-foreground">({it.categoria})</span></p>
                    {it.resultados.length > 0 && (
                      <table className="mt-1 w-full text-[11px]">
                        <tbody>
                          {it.resultados.map((rv, j) => (
                            <tr key={j} className={rv.flag && rv.flag !== 'normal' ? 'text-red-600' : ''}>
                              <td className="pr-2">{rv.parametro}</td>
                              <td className="pr-2 font-medium">{rv.valor} {rv.unidad}</td>
                              <td className="text-muted-foreground">
                                {rv.ref_min != null && rv.ref_max != null ? `ref ${rv.ref_min}–${rv.ref_max}` : ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Dato({ l, v }: { l: string; v: string | number | null | undefined }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{l}</div>
      <div className="whitespace-pre-wrap">{v != null && v !== '' ? String(v) : '—'}</div>
    </div>
  );
}

function Bloque({ l, v }: { l: string; v: string | null }) {
  if (!v) return null;
  return <p className="text-xs"><b>{l}:</b> <span className="whitespace-pre-wrap">{v}</span></p>;
}
